// This is https://www.npmjs.com/package/react-tiny-collapse but with added support for changing closed height
import React, { Component, ReactNode, CSSProperties } from 'react';

interface CollapseProps {
  animateChildren?: boolean;
  children?: ReactNode;
  className?: string;
  component?: string | React.ComponentType<unknown>;
  componentProps?: Record<string, unknown> & { style?: CSSProperties };
  duration?: number;
  easing?: string;
  forceInitialAnimation?: boolean;
  isOpen?: boolean;
  onMeasure?: (height: number) => void;
  unmountChildren?: boolean;
  closedHeight?: number;
}

interface CollapseState {
  height: number | null;
  isAnimating: boolean;
  isMounted: boolean;
  isOpen: boolean;
  shouldAnimate?: boolean;
}

class Collapse extends Component<CollapseProps, CollapseState> {
  static defaultProps = {
    animateChildren: false,
    component: 'div',
    componentProps: {},
    duration: 500,
    easing: 'cubic-bezier(0.3,0,0,1)',
    forceInitialAnimation: false,
    isOpen: false,
    onMeasure: () => {},
    unmountChildren: false,
    closedHeight: 0
  };

  state: CollapseState = {
    height: this.props.forceInitialAnimation || !this.props.isOpen ? this.props.closedHeight! : null,
    isAnimating: false,
    isMounted: false,
    isOpen: this.props.isOpen!
  };

  previousHeight: number = this.props.closedHeight!;
  raf: number | null = null;
  timer: NodeJS.Timeout | null = null;
  wrapper: HTMLElement | null = null;
  isAnimating: boolean = false;

  getHeight = (): number => {
    const child = this.wrapper && this.wrapper.firstElementChild;
    return child
      ? Math.max((child as HTMLElement).offsetHeight - 1, this.props.closedHeight!)
      : this.props.closedHeight!; // - 1px to avoid jump after transition
  };

  transition = (): void => {
    const newHeight = this.props.isOpen ? this.getHeight() : this.props.closedHeight!;

    if (this.timer) clearTimeout(this.timer);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.isAnimating = true;
    this.setState({ height: this.previousHeight, isAnimating: true, shouldAnimate: false }, () => {
      this.previousHeight = newHeight;
      this.raf = requestAnimationFrame(() => {
        this.raf = requestAnimationFrame(() => {
          this.setState({ height: newHeight }, () => {
            this.timer = setTimeout(() => {
              this.setState(
                {
                  height: this.props.isOpen ? null : this.props.closedHeight!,
                  isAnimating: false
                },
                () => {
                  this.isAnimating = false;
                }
              );
            }, this.props.duration);
          });
        });
      });
    });
  };

  componentDidMount(): void {
    this.setState({ isMounted: true }, () => {
      if (this.props.forceInitialAnimation) {
        this.transition();
      } else {
        this.previousHeight = this.props.isOpen ? this.getHeight() : this.props.closedHeight!;
        this.setState({ isAnimating: false });
      }
    });
  }

  onTransitionEnd = (): void => {
    this.previousHeight = this.getHeight();
  };

  static getDerivedStateFromProps(nextProps: CollapseProps, prevState: CollapseState): Partial<CollapseState> {
    const openDidChange = nextProps.isOpen !== prevState.isOpen;
    const forceAnimation = !prevState.isMounted && nextProps.forceInitialAnimation && nextProps.isOpen;

    return {
      isOpen: nextProps.isOpen!,
      shouldAnimate: openDidChange || forceAnimation
    };
  }

  getSnapshotBeforeUpdate(): number {
    return this.props.isOpen ? this.getHeight() : this.props.closedHeight!;
  }

  componentDidUpdate(prevProps: CollapseProps, prevState: CollapseState, prevHeight: number): void {
    if (this.isAnimating && !this.state.shouldAnimate) {
      return;
    }

    const newHeight = this.getHeight();
    const childrenDidChange = newHeight !== prevHeight;
    this.props.onMeasure!(newHeight);

    if (this.state.shouldAnimate || (childrenDidChange && this.props.animateChildren)) {
      this.transition();
    } else if (childrenDidChange) {
      this.previousHeight = this.getHeight();
    }
  }

  componentWillUnmount(): void {
    if (this.timer) clearTimeout(this.timer);
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  render(): ReactNode {
    const { isAnimating, isMounted } = this.state;
    const { forceInitialAnimation, isOpen, unmountChildren } = this.props;
    const shouldMount = unmountChildren ? isOpen || isAnimating : true;
    const initiallyHidden = !isMounted && forceInitialAnimation && isOpen;

    return React.createElement(
      this.props.component!,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      Object.assign({}, this.props.componentProps, {
        className: this.props.className,
        onTransitionEnd: this.onTransitionEnd,
        ref: (el: HTMLElement | null) => (this.wrapper = el),
        style: {
          ...this.props.componentProps?.style,
          height: this.state.height,
          overflow: isAnimating || !isOpen || initiallyHidden ? 'hidden' : undefined,
          visibility: (!isAnimating && !isOpen) || initiallyHidden ? 'hidden' : undefined,
          transition:
            isAnimating || initiallyHidden ? `height ${this.props.duration}ms ${this.props.easing}` : undefined
        } as CSSProperties
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any,
      shouldMount && this.props.children
    );
  }
}

export default Collapse;
